import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

function repeat(count: number) {
  return Array.from({ length: Math.max(count, 0) });
}

/**
 * Skeleton for a data table: a header strip plus evenly spaced body rows,
 * wrapped in the same bordered panel the real `<Table>` sits in. Use in place
 * of a centered `<Loader />` for content that resolves into a table.
 */
export function TableSkeleton({
  rows = 8,
  columns = 4,
  className,
}: {
  rows?: number;
  columns?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-lg border border-border",
        className
      )}
    >
      <div className="flex items-center gap-4 border-b border-border bg-muted/40 px-4 py-3">
        {repeat(columns).map((_, index) => (
          <Skeleton
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder grid
            key={index}
            className={cn("h-3.5", index === 0 ? "w-32" : "flex-1")}
          />
        ))}
      </div>
      <div className="divide-y divide-border">
        {repeat(rows).map((_, rowIndex) => (
          <div
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder grid
            key={rowIndex}
            className="flex items-center gap-4 px-4 py-3.5"
          >
            {repeat(columns).map((_, colIndex) => (
              <Skeleton
                // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder grid
                key={colIndex}
                className={cn(
                  "h-4",
                  colIndex === 0 && "w-32",
                  colIndex !== 0 && colIndex === columns - 1 && "w-16",
                  colIndex !== 0 && colIndex !== columns - 1 && "flex-1"
                )}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for a single content card: a title line and a few body lines inside
 * a bordered panel.
 */
export function CardSkeleton({
  lines = 3,
  className,
}: {
  lines?: number;
  className?: string;
}) {
  return (
    <div
      className={cn("space-y-3 rounded-lg border border-border p-6", className)}
    >
      <Skeleton className="h-5 w-40" />
      <div className="space-y-2 pt-1">
        {repeat(lines).map((_, index) => (
          <Skeleton
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder grid
            key={index}
            className={cn("h-4", index === lines - 1 ? "w-2/3" : "w-full")}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton for a list of rows (avatar + two text lines + trailing control),
 * for content that resolves into a feed or directory list.
 */
export function ListSkeleton({
  rows = 5,
  className,
}: {
  rows?: number;
  className?: string;
}) {
  return (
    <div className={cn("space-y-3", className)}>
      {repeat(rows).map((_, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed-length placeholder grid
          key={index}
          className="flex items-center gap-4 rounded-lg border border-border p-4"
        >
          <Skeleton className="size-10 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-8 w-20 shrink-0" />
        </div>
      ))}
    </div>
  );
}

/**
 * Skeleton for a two-column detail layout: a sidebar card next to a stack of
 * content cards. `gridClassName` should carry the same `lg:grid-cols-[…]` track
 * definition the real content uses so the columns line up.
 */
export function DetailSkeleton({
  gridClassName = "lg:grid lg:grid-cols-[18rem_minmax(0,1fr)] lg:items-start",
  className,
}: {
  gridClassName?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "space-y-6 lg:space-y-0",
        gridClassName,
        "lg:gap-6",
        className
      )}
    >
      <CardSkeleton lines={4} />
      <div className="space-y-6">
        <CardSkeleton lines={3} />
        <CardSkeleton lines={5} />
      </div>
    </div>
  );
}
